import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import ShareModal from '../components/ShareModal';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import {
  Package, Plus, TrendingUp, TrendingDown, RefreshCw, Edit2, Trash2,
  DollarSign, Calendar, Moon, Sun, AlertTriangle, Camera, ChevronRight, ExternalLink, Search, Zap, Minus, ChevronDown, Download,
  Bell, BellOff, FolderOpen, Tag, Filter, Lock, Share2
} from 'lucide-react';
import { format, differenceInHours, parseISO } from 'date-fns';
import axios from 'axios';
import { trackItemAddedToInventory, trackShareCreated } from '../utils/analytics';

// eBay Affiliate Campaign ID
const EBAY_CAMPAIGN_ID = '5339135784';

// Helper to parse date string as local date (avoids timezone shift)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  // If it's just a date (YYYY-MM-DD), parse as local
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise parse as ISO
  return parseISO(dateStr);
};

const buildEbaySearchUrl = (keywords) => {
  const encodedKeywords = encodeURIComponent(keywords);
  // Added _sop=15 for sort by "Price + Shipping: lowest first"
  // LH_BIN=1 for Buy It Now only
  return `https://www.ebay.com/sch/i.html?_nkw=${encodedKeywords}&_sacat=0&rt=nc&campid=${EBAY_CAMPAIGN_ID}&LH_BIN=1&_sop=15`;
};

const STORAGE_KEY = 'flipvalue_inventory';
const LAST_REFRESH_KEY = 'flipvalue_inventory_last_refresh';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, token } = useAuth();
  const { promptForInventory, addToLocalInventory, recordAction } = useGuest();
  
  const isGuest = !user;
  const isFreeUser = !user || user.subscription_tier === 'free' || (!user.subscription_tier && !user.subscription?.tier);
  const isPro = user?.subscription?.tier === 'pro' || user?.subscription?.tier === 'lifetime_pro';
  
  // Track inventory usage
  const trackInventoryUsage = async () => {
    if (isGuest) {
      recordAction();
    } else if (token) {
      try {
        await axios.post(
          `${API}/usage/track?usage_type=inventory_added`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to track inventory usage:', error);
      }
    }
  };
  
  const [inventory, setInventory] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [editingQuantityId, setEditingQuantityId] = useState(null);
  const [tempQuantity, setTempQuantity] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showConfirmResults, setShowConfirmResults] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [prefillValue, setPrefillValue] = useState(null);
  const [prefillPhoto, setPrefillPhoto] = useState(null);
  
  // For undo functionality - store inventory snapshot before delete
  const deletedItemRef = useRef(null);
  const inventoryBeforeDeleteRef = useRef(null);
  
  // Pre-fill from scan result
  const prefillData = location.state || {};

  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    costPrice: '',
    quantity: '1',
    dateAcquired: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
    userCategory: '',
    condition: 'New', // Add condition field
    alertSettings: {
      enabled: false,
      threshold_percent: 10,
      direction: 'both',
      email_enabled: true
    }
  });
  
  // Category and filter state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertItem, setAlertItem] = useState(null);

  // Load inventory from backend (or localStorage for guests)
  useEffect(() => {
    const loadInventory = async () => {
      setLoading(true);
      
      if (user && token) {
        // Logged in - load from backend
        try {
          const response = await axios.get(`${API}/inventory`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Map backend fields to frontend fields
          const items = (response.data.items || []).map(item => ({
            ...item,
            currentValue: item.estimated_value || item.currentValue || 0,
            costPrice: item.purchase_price || item.costPrice || 0,
            photo: item.image_url || item.photo || '',
            dateAcquired: item.purchase_date || item.dateAcquired || new Date().toISOString()
          }));
          setInventory(items);
          
          // Load categories
          try {
            const catResponse = await axios.get(`${API}/inventory/categories`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(catResponse.data.categories || []);
          } catch (catError) {
            console.error('Failed to load categories:', catError);
          }
          
          // Load subscription for sharing
          try {
            const subResponse = await axios.get(`${API}/billing/subscription`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setSubscription(subResponse.data.subscription);
          } catch (subError) {
            console.error('Failed to load subscription:', subError);
          }
          
          // Check if there's localStorage data to sync
          const localData = localStorage.getItem(STORAGE_KEY);
          if (localData) {
            const localItems = JSON.parse(localData);
            if (localItems.length > 0 && items.length === 0) {
              // Offer to sync localStorage data to backend
              setSyncing(true);
            }
          }
        } catch (error) {
          console.error('Failed to load inventory:', error);
          // Fall back to localStorage
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            setInventory(JSON.parse(saved));
          }
        }
      } else {
        // Guest - load from localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          setInventory(JSON.parse(saved));
        }
      }
      
      const lastRefreshDate = localStorage.getItem(LAST_REFRESH_KEY);
      if (lastRefreshDate) {
        setLastRefresh(new Date(lastRefreshDate));
      }
      setLoading(false);
    };
    
    loadInventory();
  }, [user, token]);

  // Handle pre-fill from scan - directly add item if value is provided
  useEffect(() => {
    if (prefillData.itemName && !isAddModalOpen) {
      // If coming from search results with estimated value, skip confirmation
      if (prefillData.estimatedValue && prefillData.estimatedValue > 0) {
        setFormData(prev => ({
          ...prev,
          name: prefillData.itemName,
          photo: prefillData.photo || '',
          condition: prefillData.condition || 'New'
        }));
        // Store the prefilled value to use when saving
        setPrefillValue(prefillData.estimatedValue);
        setPrefillPhoto(prefillData.photo || '');
      } else {
        setFormData(prev => ({
          ...prev,
          name: prefillData.itemName,
          photo: prefillData.photo || '',
          condition: prefillData.condition || 'New'
        }));
      }
      setIsAddModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // Save inventory (backend for logged in, localStorage for guests)
  const saveInventory = async (newInventory, itemToUpdate = null) => {
    setInventory(newInventory);
    
    // Always save to localStorage as backup
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
    
    // If logged in and we have a specific item to update, sync to backend
    if (user && token && itemToUpdate) {
      try {
        await axios.put(`${API}/inventory/${itemToUpdate.id}`, {
          estimated_value: itemToUpdate.currentValue,
          image_url: itemToUpdate.photo
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to sync item to backend:', error);
      }
    }
  };
  
  // Sync localStorage data to backend
  const syncLocalToBackend = async () => {
    if (!user || !token) return;
    
    const localData = localStorage.getItem(STORAGE_KEY);
    if (!localData) return;
    
    const localItems = JSON.parse(localData);
    if (localItems.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/inventory/sync`, localItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Imported ${response.data.imported} items from local storage`);
      
      // Clear localStorage after successful sync
      localStorage.removeItem(STORAGE_KEY);
      
      // Reload inventory from backend
      const inventoryResponse = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(inventoryResponse.data.items || []);
      setSyncing(false);
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync inventory');
    }
  };
  
  // Export to CSV (Coming Soon)
  const exportToCSV = async () => {
    toast.info('CSV Export is coming soon!');
  };

  // Category management
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    // Just add to local list - categories are stored on items
    const exists = categories.find(c => c.name.toLowerCase() === newCategoryName.toLowerCase());
    if (exists) {
      toast.error('Category already exists');
      return;
    }
    
    setCategories([...categories, { name: newCategoryName.trim(), count: 0 }]);
    setNewCategoryName('');
    toast.success(`Category "${newCategoryName}" created`);
    setShowCategoryModal(false);
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!user || !token) return;
    
    try {
      await axios.delete(`${API}/inventory/categories/${encodeURIComponent(categoryName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setCategories(categories.filter(c => c.name !== categoryName));
      setInventory(inventory.map(item => 
        item.userCategory === categoryName ? { ...item, userCategory: null } : item
      ));
      
      if (selectedCategory === categoryName) {
        setSelectedCategory('all');
      }
      
      toast.success(`Category "${categoryName}" removed`);
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Price alert management
  const handleUpdateAlerts = async (item, settings) => {
    if (!user || !token) {
      toast.error('Please sign in to use price alerts');
      return;
    }
    
    try {
      await axios.put(`${API}/inventory/${item.id}/alerts`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local state
      setInventory(inventory.map(i => 
        i.id === item.id ? { ...i, alertSettings: settings } : i
      ));
      
      toast.success(settings.enabled ? 'Price alert enabled' : 'Price alert disabled');
      setShowAlertModal(false);
      setAlertItem(null);
    } catch (error) {
      toast.error('Failed to update alerts');
    }
  };

  // Filter inventory by category
  const filteredInventory = useMemo(() => {
    if (selectedCategory === 'all') return inventory;
    if (selectedCategory === 'uncategorized') {
      return inventory.filter(item => !item.userCategory);
    }
    return inventory.filter(item => item.userCategory === selectedCategory);
  }, [inventory, selectedCategory]);

  // Refresh item value - simplified to match Search functionality
  const refreshItemValue = async (item) => {
    try {
      // Call search API - same as Search feature, but skip saving to history
      const response = await axios.post(`${API}/search`, {
        query: item.name,
        condition: 'New',
        skip_history: true  // Don't save inventory refreshes to search history
      });
      
      // Get value directly from API response - same as Search page
      const newValue = response.data.average_price || 0;
      
      // If no results with New, try Used
      if (newValue === 0) {
        const usedResponse = await axios.post(`${API}/search`, {
          query: item.name,
          condition: 'Used',
          skip_history: true  // Don't save inventory refreshes to search history
        });
        const usedValue = usedResponse.data.average_price || 0;
        if (usedValue === 0) {
          return { ...item, currentValue: 0 };
        }
        // Get thumbnail from Used results
        const comps = usedResponse.data.comps || [];
        const thumbnail = comps[0]?.image_url || item.photo;
        return { ...item, currentValue: usedValue, photo: item.photo?.startsWith('data:') ? item.photo : thumbnail };
      }
      
      // Get thumbnail from New results
      const comps = response.data.comps || [];
      const thumbnail = comps[0]?.image_url || item.photo;
      
      return {
        ...item,
        currentValue: newValue,
        photo: item.photo?.startsWith('data:') ? item.photo : thumbnail,
        lastRefreshed: new Date().toISOString()
      };
    } catch (error) {
      console.error('Refresh error:', error);
      return item;
    }
  };

  // Refresh all items
  const refreshAllValues = useCallback(async (silent = false) => {
    if (inventory.length === 0) return;
    
    setRefreshing(true);
    const updatedInventory = [];
    
    for (const item of inventory) {
      const updated = await refreshItemValue(item);
      updatedInventory.push(updated);
      
      // Sync each updated item to backend if logged in
      if (user && token && updated.currentValue !== item.currentValue) {
        try {
          await axios.put(`${API}/inventory/${updated.id}`, {
            estimated_value: updated.currentValue,
            image_url: updated.photo
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to sync item:', error);
        }
      }
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedInventory));
    setInventory(updatedInventory);
    const now = new Date();
    localStorage.setItem(LAST_REFRESH_KEY, now.toISOString());
    setLastRefresh(now);
    setRefreshing(false);
    
    if (!silent) {
      toast.success('Values updated!');
    }
  }, [inventory, user, token]);

  // Auto-refresh values on page load if older than 24 hours
  useEffect(() => {
    const autoRefreshIfNeeded = async () => {
      if (inventory.length === 0) return;
      
      const lastRefreshDate = localStorage.getItem(LAST_REFRESH_KEY);
      if (lastRefreshDate) {
        const hoursSinceRefresh = differenceInHours(new Date(), new Date(lastRefreshDate));
        if (hoursSinceRefresh < 24) return;
      }
      
      // Auto-refresh all items
      await refreshAllValues(true);
    };
    
    if (inventory.length > 0) {
      autoRefreshIfNeeded();
    }
  }, [inventory.length, refreshAllValues]);

  // Check for "Time to Sell" alert
  const shouldSellAlert = (item) => {
    if (!item.priceHistory || item.priceHistory.length < 7) return false;
    
    const recent = item.priceHistory.slice(-7);
    const older = item.priceHistory.slice(-30, -7);
    
    if (older.length === 0) return false;
    
    const recentAvg = recent.reduce((sum, p) => sum + p.value, 0) / recent.length;
    const olderMax = Math.max(...older.map(p => p.value));
    
    // Alert if recent average is 20%+ higher than previous peak
    return recentAvg > olderMax * 1.2;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const quantity = parseInt(formData.quantity) || 1;
    const itemId = editingItem?.id || crypto.randomUUID();
    
    // Guest mode limit: 5 items max
    const GUEST_INVENTORY_LIMIT = 5;
    if (!user && !editingItem && inventory.length >= GUEST_INVENTORY_LIMIT) {
      toast.error(`Guest accounts are limited to ${GUEST_INVENTORY_LIMIT} items. Sign up for unlimited inventory!`);
      return;
    }
    
    const newItem = {
      id: itemId,
      name: formData.name,
      photo: formData.photo,
      costPrice: parseFloat(formData.costPrice) || 0,
      quantity: quantity,
      dateAcquired: formData.dateAcquired,
      notes: formData.notes,
      condition: formData.condition || 'New', // Include condition
      userCategory: formData.userCategory || null,
      alertSettings: formData.alertSettings,
      currentValue: editingItem?.currentValue || prefillData.estimatedValue || 0,
      lastRefreshed: editingItem?.lastRefreshed || null,
      priceHistory: editingItem?.priceHistory || (prefillData.estimatedValue ? [{
        date: format(new Date(), 'yyyy-MM-dd'),
        value: prefillData.estimatedValue
      }] : []),
      createdAt: editingItem?.createdAt || new Date().toISOString()
    };

    // If editing, just update directly
    if (editingItem) {
      const newInventory = inventory.map(i => i.id === editingItem.id ? newItem : i);
      setInventory(newInventory);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
      
      // Sync to backend if logged in
      if (user && token) {
        try {
          await axios.put(`${API}/inventory/${editingItem.id}`, {
            name: newItem.name,
            category: newItem.userCategory || 'collectibles',
            user_category: newItem.userCategory || null,  // For custom category filtering
            purchase_price: newItem.costPrice,
            purchase_date: newItem.dateAcquired,
            estimated_value: newItem.currentValue,
            image_url: newItem.photo,
            notes: newItem.notes,
            alert_settings: newItem.alertSettings
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to sync edit to backend:', error);
        }
      }
      
      resetForm();
      toast.success('Item updated!');
      return;
    }

    // For new items - check if we have prefilled value from search results
    setIsAddModalOpen(false);
    
    // If we have prefilled value from search results, skip the confirmation flow
    if (prefillValue && prefillValue > 0) {
      const value = prefillValue;
      const photo = prefillPhoto || newItem.photo;
      setPrefillValue(null);
      setPrefillPhoto(null);
      // Clear location state
      window.history.replaceState({}, document.title);
      finalizeAddItem(newItem, value, photo);
      return;
    }
    
    try {
      // Use skip_history to avoid duplicate entries in search history
      const response = await axios.post(`${API}/search`, { 
        query: newItem.name, 
        condition: newItem.condition || 'New',
        skip_history: true
      });
      
      // Store pending item and show confirmation
      setPendingItem(newItem);
      setSearchResults(response.data);
      setShowConfirmResults(true);
    } catch (error) {
      // If search fails, still add item but with $0 value
      toast.error('Could not fetch value. Adding item anyway.');
      finalizeAddItem(newItem, 0, '');
    }
  };
  
  // Finalize adding item after confirmation
  const finalizeAddItem = async (item, value, thumbnail) => {
    const finalItem = { 
      ...item, 
      currentValue: value, 
      photo: thumbnail || item.photo 
    };
    
    // Check guest limit
    if (!user && inventory.length >= 5) {
      toast.error('Guest limit reached (5 items). Sign up for unlimited inventory!');
      return;
    }
    
    // Add to inventory
    if (!user) {
      addToLocalInventory(finalItem);
      const newInventory = [...inventory, finalItem];
      setInventory(newInventory);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
    } else {
      // Logged in - save to backend
      try {
        const response = await axios.post(`${API}/inventory`, {
          name: finalItem.name,
          brand: null,
          category: finalItem.userCategory || 'collectibles',
          user_category: finalItem.userCategory || null,  // For custom category filtering
          condition: 'New',
          purchase_price: finalItem.costPrice,
          purchase_date: finalItem.dateAcquired,
          estimated_value: finalItem.currentValue,
          image_url: finalItem.photo,
          notes: finalItem.notes,
          status: 'active',
          alert_settings: finalItem.alertSettings
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update local state with backend-generated ID
        const savedItem = {
          ...finalItem,
          id: response.data.id || finalItem.id
        };
        const newInventory = [...inventory, savedItem];
        setInventory(newInventory);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
      } catch (error) {
        console.error('Failed to save to backend:', error);
        toast.error('Failed to save item. Please try again.');
        return;
      }
    }
    
    setShowConfirmResults(false);
    setPendingItem(null);
    setSearchResults(null);
    resetForm();
    
    // Track the add to inventory event
    trackItemAddedToInventory(formData.name, formData.purchasePrice);
    
    // Don't show value in toast for Free users (privacy/paywall)
    toast.success('Item added to inventory!');
  };
  
  // Confirm results and add item
  const confirmAddItem = async () => {
    if (pendingItem && searchResults) {
      // Track usage when user confirms
      await trackInventoryUsage();
      
      const value = searchResults.average_price || 0;
      const thumbnail = searchResults.comps?.[0]?.image_url || '';
      
      // Guest flow
      if (!user) {
        promptForInventory(() => {
          finalizeAddItem(pendingItem, value, thumbnail);
        });
      } else {
        finalizeAddItem(pendingItem, value, thumbnail);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      photo: '', 
      costPrice: '', 
      quantity: '1', 
      dateAcquired: format(new Date(), 'yyyy-MM-dd'), 
      notes: '',
      condition: 'New',
      userCategory: '',
      alertSettings: {
        enabled: false,
        threshold_percent: 10,
        direction: 'both',
        email_enabled: true
      }
    });
    setEditingItem(null);
    setIsAddModalOpen(false);
    // Clear location state
    window.history.replaceState({}, document.title);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      photo: item.photo || '',
      costPrice: item.costPrice.toString(),
      quantity: (item.quantity || 1).toString(),
      dateAcquired: item.dateAcquired,
      notes: item.notes || '',
      condition: item.condition || 'New',
      userCategory: item.userCategory || '',
      alertSettings: item.alertSettings || {
        enabled: false,
        threshold_percent: 10,
        direction: 'both',
        email_enabled: true
      }
    });
    setIsAddModalOpen(true);
  };

  const handleDelete = async (item, e) => {
    if (e) e.stopPropagation();
    
    // Store deleted item and current inventory for undo
    deletedItemRef.current = item;
    inventoryBeforeDeleteRef.current = [...inventory];
    
    // Remove from inventory locally
    const newInventory = inventory.filter(i => i.id !== item.id);
    setInventory(newInventory);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
    if (selectedItem?.id === item.id) setSelectedItem(null);
    
    // Delete from backend if logged in
    if (user && token) {
      try {
        await axios.delete(`${API}/inventory/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to delete from backend:', error);
      }
    }
    
    // Show toast with undo (4 second duration)
    toast('Item deleted', {
      icon: <Trash2 className="w-4 h-4 text-red-500" />,
      action: {
        label: 'Undo',
        onClick: () => undoDelete()
      },
      duration: 4000
    });
  };

  const undoDelete = async () => {
    // Use the stored inventory snapshot to restore
    if (inventoryBeforeDeleteRef.current && deletedItemRef.current) {
      setInventory(inventoryBeforeDeleteRef.current);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(inventoryBeforeDeleteRef.current));
      
      // Re-add to backend if logged in
      if (user && token) {
        const item = deletedItemRef.current;
        try {
          await axios.post(`${API}/inventory`, {
            name: item.name,
            brand: null,
            category: item.userCategory || 'collectibles',
            user_category: item.userCategory || null,  // For custom category filtering
            condition: 'New',
            purchase_price: item.costPrice,
            purchase_date: item.dateAcquired,
            estimated_value: item.currentValue,
            image_url: item.photo,
            notes: item.notes,
            status: 'active',
            alert_settings: item.alertSettings
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to restore to backend:', error);
        }
      }
      
      toast.success('Item restored!', { duration: 4000 });
    }
    // Clear refs
    deletedItemRef.current = null;
    inventoryBeforeDeleteRef.current = null;
  };

  // Quick quantity update
  const updateQuantity = (item, newQuantity) => {
    const qty = Math.max(1, parseInt(newQuantity) || 1);
    const updatedItem = { ...item, quantity: qty };
    const newInventory = inventory.map(i => i.id === item.id ? updatedItem : i);
    saveInventory(newInventory);
    setEditingQuantityId(null);
    setTempQuantity('');
  };

  // Quick increment/decrement quantity (save immediately)
  const adjustQuantity = (item, delta, e) => {
    if (e) e.stopPropagation();
    const currentQty = item.quantity || 1;
    const newQty = Math.max(1, currentQty + delta);
    const updatedItem = { ...item, quantity: newQty };
    const newInventory = inventory.map(i => i.id === item.id ? updatedItem : i);
    saveInventory(newInventory);
    // Update selected item if it's the one being modified
    if (selectedItem?.id === item.id) {
      setSelectedItem(updatedItem);
    }
  };

  // Navigate to profit calculator with pre-filled data
  const openProfitCalculator = (item, e) => {
    if (e) e.stopPropagation();
    
    const quantity = item.quantity || 1;
    const costPerItem = item.costPrice / quantity;
    
    navigate('/calculator', {
      state: {
        itemName: item.name,
        estimatedValue: item.currentValue,
        costPrice: costPerItem,
        fromInventory: true
      }
    });
  };

  // Inventory stats with quantity
  const totalItems = inventory.reduce((sum, i) => sum + (i.quantity || 1), 0);
  const totalCost = inventory.reduce((sum, i) => sum + (i.costPrice * (i.quantity || 1)), 0);
  const totalValue = inventory.reduce((sum, i) => sum + ((i.currentValue || 0) * (i.quantity || 1)), 0);
  const totalGainLoss = totalValue - totalCost;
  const gainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="text-left">
                <h1 className="text-base font-bold leading-tight">FlipValue</h1>
                <p className="text-[10px] text-muted-foreground -mt-0.5">Personal Inventory</p>
              </div>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refreshAllValues()}
              disabled={refreshing || inventory.length === 0}
              className="p-2 rounded-lg hover:bg-secondary disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="w-full px-4 py-4 flex-1">
        {/* Last Refresh Info */}
        {lastRefresh && (
          <p className="text-[10px] text-muted-foreground text-center mb-2">
            Values updated {format(lastRefresh, 'MMM d, h:mm a')}
          </p>
        )}

        {/* Inventory Summary */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-card rounded-xl border border-border p-3">
            <p className="text-[10px] text-muted-foreground">Total Items</p>
            <p className="text-xl font-bold">{totalItems}</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-3">
            <p className="text-[10px] text-muted-foreground">Cost Basis</p>
            <p className={`text-xl font-bold font-mono ${isGuest ? 'blur-sm select-none' : ''}`}>
              {isGuest ? '$XXX.XX' : formatCurrency(totalCost)}
            </p>
          </div>
          <div className="bg-card rounded-xl border border-border p-3">
            <p className="text-[10px] text-muted-foreground">Current Value</p>
            <p className={`text-xl font-bold font-mono text-primary ${isGuest ? 'blur-sm select-none' : ''}`}>
              {isGuest ? '$XXX.XX' : formatCurrency(totalValue)}
            </p>
          </div>
          <div className={`rounded-xl border p-3 ${
            totalGainLoss >= 0 
              ? 'bg-green-500/10 border-green-500/20' 
              : 'bg-red-500/10 border-red-500/20'
          }`}>
            <p className="text-[10px] text-muted-foreground">Gain/Loss</p>
            <div className="flex items-center gap-1">
              {totalGainLoss >= 0 ? (
                <TrendingUp className={`w-4 h-4 text-green-600 ${isGuest ? 'opacity-30' : ''}`} />
              ) : (
                <TrendingDown className={`w-4 h-4 text-red-600 ${isGuest ? 'opacity-30' : ''}`} />
              )}
              <p className={`text-lg font-bold font-mono ${
                totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'
              } ${isGuest ? 'blur-sm select-none' : ''}`}>
                {isGuest ? 'XX.X%' : `${gainLossPercent.toFixed(1)}%`}
              </p>
            </div>
            {isGuest && (
              <p className="text-[9px] text-muted-foreground mt-1">Sign up to see values</p>
            )}
          </div>
        </div>

        {/* Refresh Inventory Button */}
        {inventory.length > 0 && (
          <button
            onClick={() => refreshAllValues()}
            disabled={refreshing}
            className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing Inventory...' : 'Refresh Inventory'}
          </button>
        )}

        {/* Add Button */}
        <div className="flex gap-2 mb-4">
          <Button 
            onClick={() => {
              const GUEST_LIMIT = 5;
              if (!user && inventory.length >= GUEST_LIMIT) {
                toast.error(`Guest limit reached (${GUEST_LIMIT} items). Sign up for unlimited inventory!`);
                return;
              }
              setIsAddModalOpen(true);
            }} 
            className="gap-2 flex-1"
          >
            <Plus className="w-4 h-4" />
            Add Item {!user && inventory.length >= 5 && '(Limit Reached)'}
          </Button>
          {user && inventory.length > 0 && (
            <Button 
              onClick={() => setIsShareModalOpen(true)}
              variant="outline"
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          )}
        </div>

        {/* Category Filter Bar */}
        {user && categories.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                All ({inventory.length})
              </button>
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    selectedCategory === cat.name 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
              <button
                onClick={() => setSelectedCategory('uncategorized')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                  selectedCategory === 'uncategorized' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                Uncategorized
              </button>
            </div>
          </div>
        )}

        {/* Inventory List */}
        {filteredInventory.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{inventory.length === 0 ? 'No items in inventory' : 'No items in this category'}</p>
            <p className="text-sm">{inventory.length === 0 ? 'Add items to track their value over time' : 'Try selecting a different category'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredInventory.map(item => {
              const quantity = item.quantity || 1;
              const totalItemCost = item.costPrice * quantity;
              const totalItemValue = (item.currentValue || 0) * quantity;
              const gainLoss = totalItemValue - totalItemCost;
              const gainLossPct = totalItemCost > 0 ? (gainLoss / totalItemCost) * 100 : 0;
              const alertSell = shouldSellAlert(item);
              const isEditingQty = editingQuantityId === item.id;

              // Handle photo upload for this item
              const handlePhotoUpload = (e) => {
                e.stopPropagation();
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = async () => {
                    const updatedItem = { ...item, photo: reader.result };
                    const newInventory = inventory.map(i => i.id === item.id ? updatedItem : i);
                    setInventory(newInventory);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
                    // Sync to backend if logged in
                    if (user && token) {
                      try {
                        await axios.put(`${API}/inventory/${item.id}`, { image_url: reader.result }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                      } catch (err) { console.error('Photo sync failed:', err); }
                    }
                    toast.success('Photo updated!');
                  };
                  reader.readAsDataURL(file);
                }
              };

              return (
                <div
                  key={item.id}
                  onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
                  className={`bg-card rounded-xl border p-3 cursor-pointer transition-colors select-none ${
                    selectedItem?.id === item.id ? 'border-primary' : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  {/* Hidden file input for this item */}
                  <input
                    type="file"
                    accept="image/*"
                    id={`photo-upload-${item.id}`}
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  
                  <div className="flex items-start gap-3">
                    {/* Thumbnail - no overlay icon */}
                    <div className="flex-shrink-0">
                      {item.photo ? (
                        <img src={item.photo} alt={item.name} className="w-14 h-14 rounded-lg object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-secondary flex items-center justify-center">
                          <Package className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0 pr-2">
                      <div className="flex items-start gap-2">
                        {/* Two lines for item title */}
                        <h3 className="font-semibold text-sm line-clamp-2 leading-tight">{item.name}</h3>
                        <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                          {quantity > 1 && (
                            <span className="text-xs px-1.5 py-0.5 bg-secondary text-muted-foreground rounded font-medium">
                              x{quantity}
                            </span>
                          )}
                          {alertSell && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-600 text-[10px] rounded-full">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Sell?
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>Cost: {formatCurrency(totalItemCost)}</span>
                        <span>•</span>
                        <span>{format(parseLocalDate(item.dateAcquired), 'MMM d')}</span>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0 pl-1">
                      <p className={`font-bold font-mono text-sm ${totalItemValue > 0 ? 'text-green-600' : 'text-muted-foreground'} ${isGuest ? 'blur-sm select-none' : ''}`}>
                        {isGuest ? '$XX.XX' : formatCurrency(totalItemValue)}
                      </p>
                      <p className={`text-xs font-mono ${
                        gainLoss >= 0 ? 'text-green-600' : 'text-red-600'
                      } ${isGuest ? 'blur-sm select-none' : ''}`}>
                        {isGuest ? '+X.X%' : `${gainLoss >= 0 ? '+' : ''}${gainLossPct.toFixed(1)}%`}
                      </p>
                      {!isGuest && totalItemValue > 0 && (
                        <p className="text-[9px] text-muted-foreground mt-0.5">Weighted avg</p>
                      )}
                      {!isGuest && totalItemValue === 0 && (
                        <p className="text-[9px] text-muted-foreground mt-0.5">Tap Refresh</p>
                      )}
                    </div>

                    {/* Alert button - Pro only */}
                    {user && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isPro) {
                            toast.error('Price alerts are a Pro feature', {
                              description: 'Upgrade to Pro to set price alerts on your inventory items',
                              action: {
                                label: 'Upgrade',
                                onClick: () => navigate('/pricing')
                              }
                            });
                            return;
                          }
                          setAlertItem({
                            ...item,
                            alertSettings: item.alertSettings || {
                              enabled: false,
                              threshold_percent: 10,
                              direction: 'both',
                              email_enabled: true
                            }
                          });
                          setShowAlertModal(true);
                        }}
                        className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                          !isPro
                            ? 'opacity-50 cursor-not-allowed hover:bg-secondary text-muted-foreground'
                            : item.alertSettings?.enabled 
                              ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                              : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                        }`}
                        title={!isPro ? 'Pro feature - Upgrade to set price alerts' : item.alertSettings?.enabled ? 'Price alert active' : 'Set price alert'}
                      >
                        {!isPro ? (
                          <Lock className="w-4 h-4" />
                        ) : item.alertSettings?.enabled ? (
                          <Bell className="w-4 h-4" />
                        ) : (
                          <BellOff className="w-4 h-4" />
                        )}
                      </button>
                    )}

                    {/* Delete button - RED color */}
                    <button
                      onClick={(e) => handleDelete(item, e)}
                      className="p-1.5 hover:bg-red-500/10 text-red-500 rounded-lg transition-colors flex-shrink-0"
                      title="Delete item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Expanded View */}
                  {selectedItem?.id === item.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      {/* Per-item info for quantity > 1 */}
                      {quantity > 1 && (
                        <div className="bg-secondary/50 rounded-lg p-2 mb-3 text-xs">
                          <p className="text-muted-foreground">Per item: {formatCurrency(item.costPrice)} cost • {formatCurrency(item.currentValue || 0)} value</p>
                        </div>
                      )}

                      {/* Change Photo and Edit Buttons - Side by Side */}
                      <div className="flex items-center gap-2 mb-3">
                        <label
                          htmlFor={`photo-upload-${item.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-border rounded-lg cursor-pointer hover:bg-secondary transition-colors"
                        >
                          <Camera className="w-4 h-4" />
                          Change Photo
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(item);
                          }}
                          className="gap-1 border-primary/50"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </Button>
                      </div>

                      {/* Quantity Controls in Expanded View */}
                      <div className="flex items-center justify-between mb-4 p-3 bg-secondary/30 rounded-lg">
                        <span className="text-sm font-medium">Quantity</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => adjustQuantity(item, -1, e)}
                            disabled={quantity <= 1}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                              quantity <= 1 
                                ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                                : 'bg-secondary hover:bg-muted text-foreground'
                            }`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center font-semibold text-lg">{quantity}</span>
                          <button
                            onClick={(e) => adjustQuantity(item, 1, e)}
                            className="w-8 h-8 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 flex items-center justify-center transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async (e) => {
                            e.stopPropagation();
                            toast.info('Refreshing value...');
                            try {
                              const updated = await refreshItemValue(item);
                              const newInventory = inventory.map(i => i.id === item.id ? updated : i);
                              setInventory(newInventory);
                              localStorage.setItem(STORAGE_KEY, JSON.stringify(newInventory));
                              if (updated.currentValue > 0) {
                                toast.success(`Value: $${updated.currentValue.toFixed(2)}`);
                              } else {
                                toast.error('No listings found. Try a shorter item name.');
                              }
                            } catch (err) {
                              console.error('Refresh error:', err);
                              toast.error('Failed to refresh');
                            }
                          }}
                          disabled={refreshing}
                          className="gap-1"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={(e) => openProfitCalculator(item, e)}
                          className="gap-1"
                        >
                          <DollarSign className="w-4 h-4" />
                          Calculate Profit
                        </Button>
                        <a
                          href={buildEbaySearchUrl(item.name)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                          >
                            <Search className="w-4 h-4" />
                            eBay
                            <ExternalLink className="w-3 h-3 ml-0.5 opacity-70" />
                          </Button>
                        </a>
                        <div className="flex-1" />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">
                        eBay link is an affiliate link – we may earn a small commission
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add to Inventory'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Item Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Nike Dunk Low Panda"
                required
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Tip: Include brand, model, size, color for accurate pricing
              </p>
            </div>

            {/* Thumbnail Upload - REMOVED per user request */}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Cost Price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.costPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, costPrice: e.target.value }))}
                    placeholder="0.00"
                    className="pl-7"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="1"
                />
              </div>
            </div>

            {/* Condition Toggle */}
            <div>
              <label className="text-sm font-medium">Condition</label>
              <div className="flex items-center gap-1 bg-secondary rounded-lg p-1 mt-1">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, condition: 'New' }))}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    formData.condition === 'New' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  New
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, condition: 'Used' }))}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                    formData.condition === 'Used' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Used
                </button>
              </div>
            </div>

            {/* Category Selector - moved above Date Acquired */}
            {user && (
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Category
                </label>
                <div className="flex gap-2 mt-1">
                  <select
                    value={formData.userCategory}
                    onChange={(e) => setFormData(prev => ({ ...prev, userCategory: e.target.value }))}
                    className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">No category</option>
                    {categories.map(cat => (
                      <option key={cat.name} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowCategoryModal(true)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium">Date Acquired</label>
              <Input
                type="date"
                value={formData.dateAcquired}
                onChange={(e) => setFormData(prev => ({ ...prev, dateAcquired: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingItem ? 'Save Changes' : 'Add Item'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Search Results Confirmation Dialog */}
      <Dialog open={showConfirmResults} onOpenChange={(open) => {
        if (!open) {
          setShowConfirmResults(false);
          setPendingItem(null);
          setSearchResults(null);
        }
      }}>
        <DialogContent className="sm:max-w-sm p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-base">Results for &quot;{pendingItem?.name}&quot;</DialogTitle>
            <p className="text-[10px] text-muted-foreground">Add brand, model, size for better results</p>
          </DialogHeader>
          <div className="space-y-2">
            {searchResults?.comps?.length > 0 ? (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {searchResults.comps.slice(0, 5).map((comp, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-1.5 rounded border border-border bg-card">
                    {comp.image_url && (
                      <img src={comp.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] line-clamp-2">{comp.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No results found</p>
            )}
            
            <div className="bg-primary/10 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Estimated Value</p>
              <p className={`text-xl font-bold text-primary ${isFreeUser ? 'blur-sm select-none' : ''}`}>
                ${searchResults?.average_price?.toFixed(2) || '0.00'}
              </p>
              {isFreeUser && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {isGuest ? 'Sign up to see values' : 'Upgrade to see values'}
                </p>
              )}
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => {
                setShowConfirmResults(false);
                setPendingItem(null);
                setSearchResults(null);
                setIsAddModalOpen(true);
              }} className="flex-1" size="sm">
                Try Again
              </Button>
              <Button onClick={confirmAddItem} className="flex-1" size="sm">
                Looks Good
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5" />
              Manage Categories
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="New category name"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
              />
              <Button onClick={handleCreateCategory}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {categories.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {categories.map(cat => (
                  <div key={cat.name} className="flex items-center justify-between p-2 rounded-lg bg-secondary/50">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{cat.name}</span>
                      <span className="text-xs text-muted-foreground">({cat.count})</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => handleDeleteCategory(cat.name)}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No categories yet. Create one above!
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Alert Modal */}
      <Dialog open={showAlertModal} onOpenChange={(open) => {
        if (!open) {
          setShowAlertModal(false);
          setAlertItem(null);
        }
      }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Price Alerts
            </DialogTitle>
          </DialogHeader>
          {alertItem && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Configure alerts for: <span className="font-medium text-foreground">{alertItem.name}</span>
              </p>
              
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Enable Price Alerts</label>
                <button
                  onClick={() => setAlertItem({
                    ...alertItem,
                    alertSettings: {
                      ...alertItem.alertSettings,
                      enabled: !alertItem.alertSettings?.enabled
                    }
                  })}
                  className={`w-11 h-6 rounded-full transition-colors ${
                    alertItem.alertSettings?.enabled ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    alertItem.alertSettings?.enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {alertItem.alertSettings?.enabled && (
                <>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Alert when price changes by:</label>
                    {/* Quick preset buttons */}
                    <div className="flex gap-2 mb-2">
                      {[5, 10, 15, 25].map(pct => (
                        <button
                          key={pct}
                          onClick={() => setAlertItem({
                            ...alertItem,
                            alertSettings: {
                              ...alertItem.alertSettings,
                              threshold_percent: pct
                            }
                          })}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            alertItem.alertSettings?.threshold_percent === pct
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                    {/* Custom input */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Custom:</span>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={alertItem.alertSettings?.threshold_percent || 10}
                        onChange={(e) => setAlertItem({
                          ...alertItem,
                          alertSettings: {
                            ...alertItem.alertSettings,
                            threshold_percent: parseInt(e.target.value) || 10
                          }
                        })}
                        className="w-16 h-8 text-sm"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">Alert direction:</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'up', label: 'Price Up', icon: TrendingUp, color: 'text-green-500', desc: 'Good time to sell' },
                        { value: 'down', label: 'Price Down', icon: TrendingDown, color: 'text-red-500', desc: 'Price dropped' },
                        { value: 'both', label: 'Any Change', icon: Bell, color: 'text-primary', desc: 'All changes' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setAlertItem({
                            ...alertItem,
                            alertSettings: {
                              ...alertItem.alertSettings,
                              direction: opt.value
                            }
                          })}
                          className={`flex-1 flex flex-col items-center gap-0.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                            alertItem.alertSettings?.direction === opt.value
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary hover:bg-secondary/80'
                          }`}
                        >
                          <opt.icon className={`w-4 h-4 ${alertItem.alertSettings?.direction === opt.value ? '' : opt.color}`} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                    <div>
                      <label className="text-sm font-medium">Email notifications</label>
                      <p className="text-xs text-muted-foreground">Get notified when alert triggers</p>
                    </div>
                    <button
                      onClick={() => setAlertItem({
                        ...alertItem,
                        alertSettings: {
                          ...alertItem.alertSettings,
                          email_enabled: !alertItem.alertSettings?.email_enabled
                        }
                      })}
                      className={`w-11 h-6 rounded-full transition-colors ${
                        alertItem.alertSettings?.email_enabled ? 'bg-primary' : 'bg-muted'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                        alertItem.alertSettings?.email_enabled ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* Info text */}
                  <p className="text-xs text-muted-foreground text-center">
                    Alerts are checked every 6 hours when you refresh item values
                  </p>
                </>
              )}

              <Button 
                className="w-full"
                onClick={() => handleUpdateAlerts(alertItem, alertItem.alertSettings)}
              >
                Save Alert Settings
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        inventory={inventory}
        categories={categories.map(c => c.name)}
        subscription={subscription}
        token={token}
      />
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
